import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  InputSignal,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UserDetailsComponent } from '../../user-details.component';
import { Router } from '@angular/router';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { combineLatest, forkJoin, map, Observable, of, startWith, switchMap } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { User, PDropdownOptions } from '../../../../interfaces';
import { UserHttpService, AuthService, EmailDeliveryPreferenceService, EmailDeliveryMode } from '../../../../services';
import { TranslationService } from '../../../../services/translation.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { API_URL, Roles } from '../../../../utils';
import { Toast } from "primeng/toast";
@Component({
  selector: 'edit-user',
  imports: [
    ReactiveFormsModule,
    NgClass,
    NgTemplateOutlet,
    ConfirmDialogModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    TranslatePipe,
    AsyncPipe,
    Toast
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class EditUserComponent {
  user: InputSignal<User> = input.required();
  private authService = inject(AuthService);
  translateService = inject(TranslationService);
  api = inject(API_URL);
  currentUser = this.authService.currentUserValue;
  currentRole = Roles.Administrator

  private fb = inject(FormBuilder);
  private userService = inject(UserHttpService);
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private emailDeliveryPreferenceService = inject(EmailDeliveryPreferenceService);

  userForm!: FormGroup;
  passwordForm!: FormGroup;
  showChangePasswordForm: boolean = false;
  emailDeliveryMode = signal<EmailDeliveryMode>('inApp');

  constructor(private translate: TranslateService) {
    effect(() => {
      this.initForm(this.user());
      this.emailDeliveryMode.set(this.emailDeliveryPreferenceService.getMode(this.user().userId));
    });
  }

  rolesOptions$: Observable<PDropdownOptions[]> = combineLatest([
    this.userService.getUserRoles(),
    this.translateService.onLangChange.pipe(startWith(null))
  ]).pipe(
    switchMap(([roles]) => {
      const isCoreactProject = this.api.includes('coreact');


      const filteredRoles = isCoreactProject
        ? roles.filter(role => role.name !== "CaseManager")
        : roles;

      if (filteredRoles.length === 0) {
        return of([]);
      }

      return forkJoin(
        filteredRoles.map(role =>
          this.translate.get(`roles.${role.name}`).pipe(
            map(translatedLabel => ({ label: translatedLabel, value: role.name }))
          )
        )
      );
    })
  );

  togglePasswordForm(show: boolean) {
    this.showChangePasswordForm = show;
  }

  setEmailDeliveryMode(mode: EmailDeliveryMode): void {
    this.emailDeliveryMode.set(mode);
    this.emailDeliveryPreferenceService.setMode(mode, this.user().userId);
  }

  initForm(user: User): void {
    this.userForm = this.fb.group({
      userId: [user.userId || '', Validators.required],
      username: [
        user.username || '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
      password: [''],
      email: [
        user.email || '',
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          Validators.maxLength(320),
        ]),
      ],
      firstName: [
        user.firstName || '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
      lastName: [
        user.lastName || '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
      role: [user.role],
      isActive: [user.isActive],
      // pic: [user.pic || ''],
    });

    this.passwordForm = this.fb.group(
      {
        userId: [user.userId || '', Validators.required],
        password: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100),
          ]),
        ],
        newPassword: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100),
          ]),
        ],
        confirmNewPassword: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100),
          ]),
        ],
      },
      {
        validator: UserDetailsComponent.MatchPassword2,
      }
    );
  }
  updatePassword(): void {
    if (
      this.passwordForm &&
      this.passwordForm.valid &&
      this.passwordForm.get('password') &&
      this.passwordForm.get('newPassword') &&
      this.passwordForm.get('confirmNewPassword')
    ) {
      const oldPasswordControl = this.passwordForm.get('password');
      const newPasswordControl = this.passwordForm.get('newPassword');
      const confirmNewPasswordControl =
        this.passwordForm.get('confirmNewPassword');

      if (
        oldPasswordControl &&
        newPasswordControl &&
        confirmNewPasswordControl &&
        oldPasswordControl.value &&
        newPasswordControl.value &&
        confirmNewPasswordControl.value
      ) {
        const oldPassword = oldPasswordControl.value;
        const newPassword = newPasswordControl.value;
        const confirmNewPassword = confirmNewPasswordControl.value;

        if (newPassword === confirmNewPassword) {
          const userId = this.passwordForm.get('userId')?.value;
          if (userId) {
            this.userService
              .updatePassword(userId, oldPassword, newPassword)
              .subscribe(
                () => {
                  //

                  this.passwordForm.reset();

                  this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('TOAST.SUCCESS_TITLE'),
                    detail: this.translate.instant('TOAST.SUCCESSFULLY_CHANGED')
                  });
                },
                (error) => {
                  console.error('Error updating password:', error);

                  this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('TOAST.ERROR_TITLE'),
                    detail: this.translate.instant('TOAST.ERROR_CHANGE_PASSWORD')
                  });
                }
              );
          } else {
            console.error('User ID is null.');
          }
        } else {
          console.error('New password and confirm new password do not match.');
        }
      } else {
        console.error('New password or confirm new password control is null.');
      }
    } else {
      console.error('User form is null or invalid.');
    }
  }
  updateUser(): void {
    if (!this.userForm.valid) {
      Object.keys(this.userForm.controls).forEach((key) => {
        //?.errors);
      });
      return;
    }

    let userToUpdate = this.userForm.value;
    userToUpdate = {
      id: userToUpdate.userId,
      username: userToUpdate.username,
      email: userToUpdate.email,
      firstName: userToUpdate.firstName,
      lastName: userToUpdate.lastName,
      isActive: userToUpdate.isActive,
      role: userToUpdate.role,
    };
    const userId = userToUpdate.id;
    //
    //

    this.userService.updateUser(userId, userToUpdate).subscribe(
      () => {
        //

        const roleControl = this.userForm.get('role');
        if (roleControl) {
          roleControl.setValue(userToUpdate.role);
        }

        this.router.navigate(['/manage-users']);
      },
      (error) => {
        console.error('Error updating user:', error);
      }
    );
  }

  onDeleteConfirmed(usrID: string) {
    this.userService.deleteUser(usrID).subscribe({
      next: (response) => {
        this.router.navigate(['/manage-users']);
        //
      },
      error(err) {
        console.error(err);
      },
    });
  }
  openConfirmationModal() {
    this.confirmationService.confirm({
      key: 'deleteConfirmDialog',
      message: 'MANAGE_USERS.DELETE_MODAL',
      header: this.translate.instant('MANAGE_USERS.DELETE_USER_HEADER'),
      closable: false,
      closeOnEscape: false,
      icon: 'fa-solid fa-triangle-exclamation',
      rejectButtonProps: {
        label: 'No, cancel!',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Yes, delete!',
        severity: 'danger',
      },
      accept: () => this.onDeleteConfirmed(this.user().userId),
    });
  }
}
